const FESTIVAL_URL = import.meta.env.VITE_FESTIVAL_SERVICE_URL || 'http://localhost:3005';

export interface FestivalVenue {
  id: string;
  name: string;
  slug: string;
  address?: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  website?: string;
  opening_hours?: Record<string, unknown>;
  images?: string[];
  avatar_url?: string;
  bio?: string;
  verified?: boolean;
  rating?: number;
  tags?: string[];
  karaoke_type: 'private' | 'public' | 'both';
  google_maps_url?: string;
}

export interface FestivalEvent {
  id: string;
  occurrence_id?: string;
  venue_id: string;
  title: string;
  description?: string;
  image_url?: string;
  starts_at: string;
  ends_at?: string;
  price?: string;
  host?: string;
  link?: string;
  event_type: 'public' | 'private';
  recurrence_frequency?: 'weekly' | 'monthly' | '';
  recurrence_day?: number;
  recurrence_end_date?: string;
}

const HTTPS_URL_RE = /^https:\/\/[a-zA-Z0-9][-a-zA-Z0-9]*(?:\.[-a-zA-Z0-9]+)+(?:\/[-a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=]*)?$/;

function sanitizeVenue(v: any): FestivalVenue {
  const website = typeof v.website === 'string' && HTTPS_URL_RE.test(v.website) ? v.website : undefined;
  return {
    ...v,
    website,
  } as FestivalVenue;
}

export async function fetchVenues(city?: string, karaokeType?: string): Promise<FestivalVenue[]> {
  const params = new URLSearchParams();
  if (city) params.set('city', city);
  if (karaokeType) params.set('karaoke_type', karaokeType);
  const res = await fetch(`${FESTIVAL_URL}/api/venues?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch venues');
  const data = await res.json();
  const venues = (data.venues || []) as any[];
  return venues.map(sanitizeVenue);
}

export async function searchVenuesByName(q: string, city?: string): Promise<FestivalVenue[]> {
  const params = new URLSearchParams({ q });
  if (city) params.set('city', city);
  const res = await fetch(`${FESTIVAL_URL}/api/venues/search?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to search venues');
  const data = await res.json();
  const venues = (data.venues || []) as any[];
  return venues.map(sanitizeVenue);
}

export async function fetchEvents(eventType?: string): Promise<FestivalEvent[]> {
  const params = new URLSearchParams();
  if (eventType) params.set('event_type', eventType);
  const from = new Date().toISOString();
  params.set('from', from);
  const res = await fetch(`${FESTIVAL_URL}/api/events?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch events');
  const data = await res.json();
  return data.events || [];
}

export async function fetchEvent(id: string): Promise<FestivalEvent> {
  const res = await fetch(`${FESTIVAL_URL}/api/events/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error('Failed to fetch event');
  return res.json();
}

export async function fetchVenueBySlug(slug: string): Promise<FestivalVenue> {
  const res = await fetch(`${FESTIVAL_URL}/api/venues/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('Failed to fetch venue');
  return sanitizeVenue(await res.json());
}

export async function fetchVenueEvents(slug: string): Promise<FestivalEvent[]> {
  const res = await fetch(`${FESTIVAL_URL}/api/venues/${encodeURIComponent(slug)}/events`);
  if (!res.ok) throw new Error('Failed to fetch venue events');
  const data = await res.json();
  return data.events || [];
}

export async function fetchCities(): Promise<string[]> {
  const res = await fetch(`${FESTIVAL_URL}/api/cities`);
  if (!res.ok) throw new Error('Failed to fetch cities');
  const data = await res.json();
  return data.cities || [];
}
